import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { GLOSSARY, GLOSSARY_LOOKUP } from '~/generated/glossary.data';
import type { GlossaryId } from '~/generated/glossary.types';
import GlossaryDefinition from '~/widgets/GlossaryDefinition/GlossaryDefinition';
import GlossaryTooltip from '~/widgets/GlossaryTooltip/GlossaryTooltip';
import { useCharacterContext } from '~/contexts/CharacterContext';
import { resolveRoll } from '~/utils/rollUtils';
import { isActionTag } from '~/utils/actionTagConfig';
import { renderTag } from '~/utils/tagRenderer';
import styles from '~/widgets/GlossaryMarkdown/GlossaryMarkdown.module.scss';

/**
 * Recognition mode for glossary term matching:
 * - 'strict': Only **bold** or [bracketed] text (current default behavior)
 * - 'primary': Bold, bracketed, OR matches glossary ID (name[0]) case-insensitive
 * - 'all': Bold, bracketed, OR matches any alias in entry.allNames case-insensitive
 */
type RecognitionMode = 'strict' | 'primary' | 'all';

interface GlossaryMarkdownProps {
  children: string;
  enableTooltips?: boolean;
  className?: string;
  components?: Partial<Components>;
  itemContext?: {
    itemName: string;
    sourceType?: 'Item' | 'Ability';
  };
  recognitionMode?: RecognitionMode;
  matchPlurality?: boolean;
}

interface TooltipState {
  termId: GlossaryId | null;
  position: { top: number; left: number };
}

interface ContentSegment {
  type: 'markdown' | 'glossary';
  content: string;
  glossaryId?: GlossaryId;
}

/**
 * Markdown renderer with automatic glossary term detection and tooltips.
 * Detects **bold text** and checks if it matches a glossary term ID.
 * If it does, renders it with a hover tooltip.
 */
export default function GlossaryMarkdown({
  children,
  enableTooltips = true,
  className,
  components: customComponents,
  itemContext,
  recognitionMode = 'strict',
  matchPlurality = false,
}: GlossaryMarkdownProps) {
  const characterContext = useCharacterContext();
  const [tooltip, setTooltip] = useState<TooltipState>({
    termId: null,
    position: { top: 0, left: 0 },
  });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const contentSegments = useMemo(() => {
    const segments: ContentSegment[] = [];
    const glossaryBlockRegex = /:::(.+?)\n([\s\S]*?)\n:::/g;

    let lastIndex = 0;
    let match;

    while ((match = glossaryBlockRegex.exec(children)) !== null) {
      const [fullMatch, name] = match;
      const matchStart = match.index;
      const id = name.trim();

      if (matchStart > lastIndex) {
        const markdownContent = children.slice(lastIndex, matchStart).trim();
        if (markdownContent) {
          segments.push({
            type: 'markdown',
            content: markdownContent,
          });
        }
      }

      if (GLOSSARY[id as GlossaryId]) {
        segments.push({
          type: 'glossary',
          content: fullMatch,
          glossaryId: id as GlossaryId,
        });
      } else {
        segments.push({
          type: 'markdown',
          content: fullMatch,
        });
      }

      lastIndex = matchStart + fullMatch.length;
    }

    if (lastIndex < children.length) {
      const markdownContent = children.slice(lastIndex).trim();
      if (markdownContent) {
        segments.push({
          type: 'markdown',
          content: markdownContent,
        });
      }
    }

    return segments.length > 0
      ? segments
      : ([{ type: 'markdown', content: children }] as ContentSegment[]);
  }, [children]);

  const handleTermHover = (termId: GlossaryId, event: React.MouseEvent<HTMLElement>) => {
    if (!enableTooltips) return;

    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      termId,
      position: {
        top: rect.bottom + 8,
        left: rect.left,
      },
    });
  };

  const handleTermLeave = () => {
    setTooltip({ termId: null, position: { top: 0, left: 0 } });
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!enableTooltips) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.classList.contains(styles.glossaryTerm)) {
          setTooltip({ termId: null, position: { top: 0, left: 0 } });
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [enableTooltips]);

  // Helper function to check if text matches a glossary term based on recognition mode
  const findGlossaryMatch = (text: string, isInBracket: boolean = false): GlossaryId | null => {
    // Mode 'strict': Only match [bracketed] or **strong** text via GLOSSARY_LOOKUP
    if (recognitionMode === 'strict') {
      const matchedId = GLOSSARY_LOOKUP[text.toLowerCase()];
      return matchedId || null;
    }

    // For modes 'primary' and 'all', we need to check if NOT in bracket
    // (brackets always use GLOSSARY_LOOKUP regardless of mode)
    if (isInBracket) {
      const matchedId = GLOSSARY_LOOKUP[text.toLowerCase()];
      return matchedId || null;
    }

    const lowerText = text.toLowerCase();

    // Mode 'primary': Match glossary ID (entry.name[0]) case-insensitive
    if (recognitionMode === 'primary') {
      for (const [id, entry] of Object.entries(GLOSSARY)) {
        if (entry.name[0].toLowerCase() === lowerText) {
          return id as GlossaryId;
        }
      }
      return null;
    }

    // Mode 'all': Match any name in entry.allNames case-insensitive
    if (recognitionMode === 'all') {
      for (const [id, entry] of Object.entries(GLOSSARY)) {
        if (entry.allNames.some(name => name.toLowerCase() === lowerText)) {
          return id as GlossaryId;
        }
      }
      return null;
    }

    return null;
  };

  // Custom renderer for strong (bold) text to detect glossary terms
  const renderStrong = ({ children }: { children?: React.ReactNode }) => {
    if (!enableTooltips) {
      // If custom strong renderer exists, use it; otherwise default
      const CustomStrong = customComponents?.strong;
      if (CustomStrong && typeof CustomStrong !== 'string') {
        return <CustomStrong>{children}</CustomStrong>;
      }
      return <strong>{children}</strong>;
    }

    const text = String(children);
    const matchedId = findGlossaryMatch(text, false); // false = not in bracket

    if (matchedId) {
      return (
        <strong
          className={styles.glossaryTerm}
          onMouseEnter={(e) => handleTermHover(matchedId, e)}
          onMouseLeave={handleTermLeave}
          onClick={(e) => handleTermHover(matchedId, e)}
          title="Hover for quick reference"
        >
          {children}
        </strong>
      );
    }

    // No match - use custom renderer if provided, otherwise default
    const CustomStrong = customComponents?.strong;
    if (CustomStrong && typeof CustomStrong !== 'string') {
      return <CustomStrong>{children}</CustomStrong>;
    }
    return <strong>{children}</strong>;
  };

  // Helper function to check if text matches any tag pattern
  // Now uses the centralized isActionTag function from actionTagConfig
  const matchesTagPattern = (text: string): boolean => {
    return isActionTag(text);
  };

  // Helper to generate plural variations of a term
  const generatePluralVariations = (term: string): string[] => {
    if (!matchPlurality) {
      return [term];
    }

    const variations = [term];

    // Common English plural rules
    // -s, -es, -ing, -ed, -er, -est
    variations.push(`${term}s`);
    variations.push(`${term}es`);
    variations.push(`${term}ing`);
    variations.push(`${term}ed`);
    variations.push(`${term}er`);
    variations.push(`${term}est`);

    // If ends with 'y', replace with 'ies'
    if (term.endsWith('y')) {
      variations.push(`${term.slice(0, -1)}ies`);
    }

    // If ends with consonant, try doubling + suffix
    const lastChar = term[term.length - 1];
    const secondLastChar = term[term.length - 2];
    if (lastChar && !/[aeiou]/.test(lastChar) && secondLastChar && /[aeiou]/.test(secondLastChar)) {
      variations.push(`${term}${lastChar}ing`);
      variations.push(`${term}${lastChar}ed`);
      variations.push(`${term}${lastChar}er`);
    }

    return variations;
  };

  // Helper to process plain text and detect glossary term substrings (for primary/all modes)
  const processPlainTextForGlossary = (text: string): React.ReactNode => {
    // Only process if we're in primary or all mode and tooltips are enabled
    if (recognitionMode === 'strict' || !enableTooltips) {
      return text;
    }

    // Build a list of all possible terms to match
    const termsToMatch: Array<{ term: string; id: GlossaryId }> = [];

    if (recognitionMode === 'primary') {
      // Match only primary names (entry.name[0])
      for (const [id, entry] of Object.entries(GLOSSARY)) {
        const variations = generatePluralVariations(entry.name[0]);
        for (const variation of variations) {
          termsToMatch.push({ term: variation, id: id as GlossaryId });
        }
      }
    } else if (recognitionMode === 'all') {
      // Match all names (entry.allNames)
      for (const [id, entry] of Object.entries(GLOSSARY)) {
        for (const name of entry.allNames) {
          const variations = generatePluralVariations(name);
          for (const variation of variations) {
            termsToMatch.push({ term: variation, id: id as GlossaryId });
          }
        }
      }
    }

    // Sort by length descending to match longer terms first
    termsToMatch.sort((a, b) => b.term.length - a.term.length);

    // Build regex pattern that matches any of the terms (case-insensitive, word boundaries)
    const escapedTerms = termsToMatch.map(({ term }) =>
      term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const pattern = new RegExp(`\\b(${escapedTerms.join('|')})\\b`, 'gi');

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      const matchStart = match.index;
      const matchedText = match[1];

      // Add text before the match (process for glossary terms in primary/all modes)
      if (matchStart > lastIndex) {
        parts.push(processPlainTextForGlossary(text.slice(lastIndex, matchStart)));
      }

      // Find the glossary ID for this match
      const matchedTerm = termsToMatch.find(
        ({ term }) => term.toLowerCase() === matchedText.toLowerCase()
      );

      if (matchedTerm) {
        parts.push(
          <span
            key={`glossary-${matchStart}`}
            className={styles.glossaryTerm}
            onMouseEnter={(e) => handleTermHover(matchedTerm.id, e)}
            onMouseLeave={handleTermLeave}
            onClick={(e) => handleTermHover(matchedTerm.id, e)}
            title="Hover for quick reference"
          >
            {matchedText}
          </span>
        );
      } else {
        parts.push(matchedText);
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? <>{parts}</> : text;
  };

  // Helper to process text nodes and detect [bracketed] glossary terms, rolls, and tags
  const processTextWithBrackets = (text: string): React.ReactNode => {
    // Regex to match [bracketed text]
    const bracketRegex = /\[([^\]]+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = bracketRegex.exec(text)) !== null) {
      const matchStart = match.index;
      const bracketedText = match[1];

      // Add text before the match (process for glossary terms in primary/all modes)
      if (matchStart > lastIndex) {
        parts.push(processPlainTextForGlossary(text.slice(lastIndex, matchStart)));
      }

      // Check for roll match (if character context available)
      const rollMatch = characterContext ? resolveRoll(bracketedText, characterContext.derived.rollableItems, characterContext.characterData.coreStats) : null;

      // Check for glossary match (if tooltips enabled)
      const glossaryMatch = enableTooltips ? findGlossaryMatch(bracketedText, true) : null; // true = in bracket

      // Case 1: Roll match → clickable roll button (takes priority)
      if (rollMatch && characterContext) {
        parts.push(
          <button
            key={`bracket-${matchStart}`}
            className={styles.clickableRoll}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                characterContext.actions.onRoll(rollMatch.name, rollMatch.pool, rollMatch.flatHits);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              characterContext.actions.onRoll(rollMatch.name, rollMatch.pool, rollMatch.flatHits);
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {rollMatch.name}
          </button>
        );
        lastIndex = match.index + match[0].length;
        continue;
      }

      // Case 2: Glossary only → tooltip on hover AND click
      if (glossaryMatch) {
        parts.push(
          <span
            key={`bracket-${matchStart}`}
            className={styles.glossaryBadge}
            onMouseEnter={(e) => handleTermHover(glossaryMatch, e)}
            onMouseLeave={handleTermLeave}
            onClick={(e) => handleTermHover(glossaryMatch, e)}
            title="Hover for quick reference"
          >
            {bracketedText}
          </span>
        );
        lastIndex = match.index + match[0].length;
        continue;
      }

      // Case 3: Check action tag (if context available)
      if (characterContext && itemContext && matchesTagPattern(bracketedText)) {
        const tagElement = renderTag(bracketedText, matchStart, {
          currentHp: characterContext.characterData.currentHp,
          currentResilience: characterContext.characterData.currentResilience,
          itemName: itemContext.itemName,
          sourceType: itemContext.sourceType,
          onTagAction: characterContext.actions.onTagAction,
        });
        parts.push(tagElement);
        lastIndex = match.index + match[0].length;
        continue;
      }

      // Case 4: Fallback - plain text with brackets
      parts.push(`[${bracketedText}]`);
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match (process for glossary terms in primary/all modes)
    if (lastIndex < text.length) {
      parts.push(processPlainTextForGlossary(text.slice(lastIndex)));
    }

    // If no matches found, process entire text for glossary terms
    return parts.length > 0 ? <>{parts}</> : processPlainTextForGlossary(text);
  };

  // Process children recursively to handle text nodes
  const processChildren = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (typeof child === 'string') {
        return processTextWithBrackets(child);
      }
      return child;
    });
  };

  // Wrapper for custom components that processes text
  const wrapComponent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component: any,
    defaultTag: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (props: any) => {
      const processedChildren = processChildren(props.children);

      if (Component && typeof Component !== 'string') {
        return <Component {...props}>{processedChildren}</Component>;
      }

      const Tag = Component || defaultTag;
      return React.createElement(Tag, props, processedChildren);
    };
  };

  // Merge custom components with glossary-aware renderers
  const mergedComponents: Components = {
    ...customComponents,
    strong: renderStrong,
    p: wrapComponent(customComponents?.p, 'p'),
    li: wrapComponent(customComponents?.li, 'li'),
    h1: wrapComponent(customComponents?.h1, 'h1'),
    h2: wrapComponent(customComponents?.h2, 'h2'),
    h3: wrapComponent(customComponents?.h3, 'h3'),
    h4: wrapComponent(customComponents?.h4, 'h4'),
    h5: wrapComponent(customComponents?.h5, 'h5'),
    h6: wrapComponent(customComponents?.h6, 'h6'),
  };

  return (
    <>
      <div className={`${styles.wrapper} ${className || ''}`}>
        {contentSegments.map((segment, index) => {
          if (segment.type === 'glossary' && segment.glossaryId) {
            const entry = GLOSSARY[segment.glossaryId];
            return <GlossaryDefinition key={`glossary-${index}`} entry={entry} recognitionMode={recognitionMode} matchPlurality={matchPlurality} />;
          }

          return (
            <ReactMarkdown key={`markdown-${index}`} components={mergedComponents}>
              {segment.content}
            </ReactMarkdown>
          );
        })}
      </div>

      {enableTooltips && tooltip.termId && createPortal(
        <div ref={tooltipRef}>
          <GlossaryTooltip
            entry={GLOSSARY[tooltip.termId]}
            position={tooltip.position}
            onClose={() => setTooltip({ termId: null, position: { top: 0, left: 0 } })}
          />
        </div>,
        document.body
      )}
    </>
  );
}
